import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is an MLP?',
    a: 'Master Limited Partnership — a publicly-traded business structure that combines tax advantages of a partnership with the liquidity of a public stock. Most MLPs operate in energy infrastructure (pipelines, storage, processing). Famous examples: Energy Transfer (ET), Enterprise Products (EPD), MPLX, Western Midstream.',
  },
  {
    q: 'Why do MLPs yield so much?',
    a: 'MLPs pay no federal corporate income tax — instead, taxable income passes through to unitholders. To maintain that pass-through status, MLPs are required to distribute roughly all their available cash. The result: high yields (7-10% is normal) backed by tangible, fee-based infrastructure assets.',
  },
  {
    q: 'What\'s a K-1 and why do people complain about it?',
    a: 'A K-1 is the IRS form MLPs send unitholders each year (instead of the 1099-DIV most stocks send). K-1s report your pro-rata share of the partnership\'s income, deductions, and credits. They\'re more complex than 1099s, often arrive late (Feb-March instead of Jan), and can require you to file state taxes in every state the MLP operates in. Tax software handles them, but most preparers charge extra.',
  },
  {
    q: 'Why are MLPs bad in IRAs?',
    a: 'Counter-intuitive answer: yes. MLPs generate "Unrelated Business Taxable Income" (UBTI). If your MLP UBTI inside an IRA exceeds $1,000 per year, the IRA owes tax on the excess — even though IRAs are supposed to be tax-deferred. The IRS designed this to prevent tax-exempt investors from arbitraging the partnership structure. The fix: hold MLPs in a taxable account, OR hold MLP ETFs (like AMLP) which wrap MLPs in a C-corp structure that avoids the UBTI problem.',
  },
  {
    q: 'How do I know if a high-yield stock is actually an MLP?',
    a: 'The ticker often (not always) ends in a letter that signals partnership structure, but the reliable check is to look at recent SEC filings or the company\'s investor relations page. Phrases like "limited partnership", "common units", or "distributions per unit" (instead of "dividends per share") are the giveaway on the issuer\'s site. Inside DiviDrip, the Triangle tab shows a distinctive amber "MLP note" ribbon at the top of any partnership ticker (Energy Transfer, Enterprise Products, MPLX, Brookfield Renewable, etc.), and the Stock Metrics tab renders a dedicated MLP Cash Lens panel with DCF-based payout numbers.',
  },
  {
    q: 'What is DCF and why do MLP investors care more about it than EPS?',
    a: 'DCF — Distributable Cash Flow — is the MLP version of FFO. Net Income plus non-cash items (mainly depreciation on pipelines) minus maintenance capex. MLP management teams use it because GAAP EPS is structurally depressed: a pipeline that earns $1 of real cash gets reported as $0.30 of GAAP earnings after the depreciation hit. EPS-based payout ratios on a healthy MLP routinely look ">100%" while the DCF-based coverage ratio is comfortably above 1.2×. The MLP Cash Lens panel in DiviDrip uses a Net Income + D&A approximation that mirrors how management presents DCF in quarterly supplementals.',
  },
  {
    q: 'My MLP has a 67/100 Triangle Score and a "trap" warning that disappeared — is the dividend OK?',
    a: 'Most likely yes — the Triangle Score blends three legs (Revenue / EPS / Dividend growth), and the EPS leg is structurally weak on MLPs because of pipeline depreciation. DiviDrip suppresses the dividend-trap warning for MLPs (the EPS-vs-dividend gap is the wrong yardstick for a partnership) and the narrative panel explicitly redirects you to the MLP Cash Lens for the cash-flow-based view. Coverage ratio above 1.2× DCF is the metric to anchor on; anything below 1.0× is a real distribution-cut risk.',
  },
];

export default function LearnMlpsExplained() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="MLPs Explained — Why That 9% Yield Comes With a K-1 Tax Form | DiviDrip"
      description="What Master Limited Partnerships are, the K-1 vs 1099-DIV difference, why MLPs are surprisingly bad in IRAs, and which MLPs are worth the tax friction."
      canonical="https://twylightcrow.com/learn/mlps-explained"
      guidePath="/learn/mlps-explained"
      sectionLabel="Vehicles & Structures"
      sectionAnchor="vehicles"
      kicker="Learn · Specialised vehicles"
      h1="MLPs Explained — High Yield, Real Assets, K-1 Forms"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs vs BDCs vs CEFs — high-yield vehicles' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — tax guide' },
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/international-dividends', label: 'International dividends — ADRs, withholding tax' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        Master Limited Partnerships are the high-yield corner of the dividend universe
        that most investors hear about, eye warily, then ignore. The 8-10% yields
        look incredible. The K-1 tax form sounds scary. So they stay on the
        sidelines.
      </p>
      <p>
        Here's what MLPs really are, when the tax friction is worth it, and the
        specific case where they're a terrible idea.
      </p>

      <aside className="my-6 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-950/30 p-4">
        <p className="text-sm font-bold text-amber-900 dark:text-amber-100 mb-1">
          A note on the Triangle Score for MLPs
        </p>
        <p className="text-xs text-amber-900/90 dark:text-amber-100/90 leading-relaxed">
          The Dividend Triangle's EPS leg looks structurally weak on most healthy MLPs —
          pipeline depreciation is a huge non-cash charge that drags GAAP earnings. DiviDrip
          handles this automatically: the dividend-trap warning is suppressed for MLPs,
          the narrative panel redirects to the MLP Cash Lens, and an amber "MLP note"
          ribbon flags the structural difference at the top of the Triangle tab.
          Skip ahead to{' '}
          <a href="#cash-coverage" className="underline font-semibold">
            "Why the EPS leg looks awful — and what to look at instead"
          </a>{' '}
          if you want the cash-coverage explanation first.
        </p>
      </aside>

      <h2>What an MLP actually is</h2>
      <p>
        Created by Congress in 1981 to encourage investment in energy infrastructure,
        Master Limited Partnerships are publicly-traded businesses structured as
        limited partnerships rather than corporations. The defining trait: they pay
        no federal corporate tax. Taxable income passes through to unitholders.
      </p>
      <p>
        In exchange for that tax break, MLPs must:
      </p>
      <ul>
        <li>Earn 90%+ of income from "qualifying" sources (mainly natural resources, real estate, or infrastructure)</li>
        <li>Distribute roughly all their available cash to unitholders</li>
        <li>Send unitholders a K-1 tax form annually instead of the standard 1099-DIV</li>
      </ul>

      <h2>Why energy infrastructure dominates the MLP space</h2>
      <p>
        Most MLPs operate pipelines, storage tanks, gas processing plants, and other
        midstream energy infrastructure. The economics fit the MLP structure perfectly:
      </p>
      <ul>
        <li><strong>Fee-based revenue</strong> — pipelines charge per barrel/cubic-foot transported, not based on commodity prices. Cash flow is steady even when oil prices crash.</li>
        <li><strong>Long-lived assets</strong> — pipelines run 30-50 years. Distributions can be calibrated against decade-long cash-flow projections.</li>
        <li><strong>High capex up front, low ongoing capex</strong> — once built, distributable cash flow is enormous because most spending is behind you.</li>
      </ul>
      <p>
        The largest MLPs (ET, EPD, MPLX, WES) operate enormous interstate pipeline
        networks that would take decades and tens of billions of dollars to replicate.
        Their dividends — sorry, distributions — are backed by tangible monopolistic
        infrastructure.
      </p>

      <h2>The K-1 form — what changes about your tax filing</h2>
      <p>
        Owning an MLP means K-1 forms instead of 1099-DIVs. Practically:
      </p>
      <ul>
        <li><strong>K-1s arrive LATE.</strong> Most by mid-March; some don't arrive until April. If you file early, you'll be filing extensions or amending.</li>
        <li><strong>Multi-state tax filing.</strong> A pipeline running through 12 states generates taxable income in those 12 states. Most filers can ignore this (most states have non-filing thresholds), but high-net-worth holders may need to file in multiple states.</li>
        <li><strong>Tax software handles them automatically</strong> if you have premium tier (TurboTax Premier, H&R Block Premium). Free tiers usually don't.</li>
        <li><strong>CPA filings cost more</strong> — typically $50-$150 extra per K-1.</li>
        <li><strong>Return-of-capital treatment.</strong> A meaningful chunk of MLP distributions is technically "return of capital", which isn't immediate taxable income — it lowers your cost basis instead. Tax-efficient in the short run; eventually catches up when you sell.</li>
      </ul>

      <h2>The IRA trap</h2>
      <p>
        Most income investors instinctively put high-yielders in Roth IRAs. With MLPs,
        this is exactly wrong.
      </p>
      <p>
        MLPs generate Unrelated Business Taxable Income (UBTI) — income from an
        active business that's "unrelated" to the IRA's tax-exempt purpose. The IRS
        designed this rule specifically to prevent tax-exempt accounts from arbitraging
        the partnership tax break.
      </p>
      <p>
        The threshold: $1,000 of UBTI per IRA per year is OK. Above that, the IRA
        itself owes tax on the excess — at trust tax rates, which max out around
        37%. The IRA custodian (Fidelity, Schwab, etc.) has to file Form 990-T on
        your behalf and pay the tax out of IRA assets.
      </p>
      <p>
        For a moderate IRA holding a few thousand dollars of MLPs, you might never
        hit the threshold. But a large MLP position absolutely will, defeating the
        Roth tax advantage entirely.
      </p>

      <h2>The MLP-ETF workaround</h2>
      <p>
        If you want MLP exposure without K-1s or UBTI: hold an MLP ETF instead. The
        most popular is <strong>AMLP</strong> (Alerian MLP ETF). Because AMLP is
        structured as a C-corp, it absorbs the partnership tax complexity internally
        and sends you a plain 1099-DIV.
      </p>
      <p>
        The trade-off: AMLP pays corporate tax on its income, which reduces the
        yield meaningfully (the underlying MLPs yield ~8-9%; AMLP yields ~7-8% after
        the wrapper tax). Worth it for IRA holders or anyone who hates K-1s; not
        worth it if you're going to hold MLPs in a taxable account anyway.
      </p>

      <h2 id="cash-coverage">Why the EPS leg looks awful — and what to look at instead</h2>
      <p>
        Open any MLP's Triangle tab — Energy Transfer (ET), Enterprise Products (EPD),
        MPLX, Western Midstream (WES) — and the EPS leg often looks like a problem.
        Reported payout ratios above 100% are routine. Earnings growth bounces around.
        On a normal C-corp, that pattern would be a flashing red trap signal.
      </p>
      <p>
        It's not, on an MLP. Here's the structural reason:
      </p>
      <ul>
        <li><strong>Pipelines and processing plants depreciate heavily on paper.</strong> A
          $5 billion pipeline carrying a 30-year useful life produces a ~$170M
          annual depreciation charge — entirely non-cash, but it lands on the income
          statement and crushes GAAP earnings.</li>
        <li><strong>The cash actually generated is much higher than reported EPS.</strong>
          MLP management teams present a non-GAAP figure called <strong>DCF —
          Distributable Cash Flow</strong> — that adds depreciation back to Net Income
          and subtracts only the recurring maintenance capex. DCF is what funds the
          distribution.</li>
        <li><strong>Healthy MLPs aim for a DCF coverage ratio of 1.2× or higher.</strong>
          That means cash generated covers the distribution with 20%+ headroom. The
          EPS-based "payout ratio" most screeners surface can show 110% while DCF
          coverage is a comfortable 1.4×.</li>
      </ul>
      <p>
        The DiviDrip Stock Metrics tab includes an <strong>MLP Cash Lens</strong>{' '}
        panel (amber border, Factory icon) that recomputes the payout ratio using a
        Net Income + Depreciation/Amortization proxy for DCF — the same construction
        management uses in their quarterly supplementals. A "Sweet Spot" verdict
        (70-85% DCF payout) is the green zone. "Stretched" (85-100%) is fine but
        watch for a coverage walk-down. "Risky" (&gt;100%) is the actual warning
        signal — that's when a distribution cut becomes a realistic risk.
      </p>
      <p>
        For the full background on GAAP vs cash-coverage metrics, see{' '}
        <Link to="/learn/payout-ratio">our payout ratio guide</Link>.
      </p>

      <h2>The MLP universe — beyond the energy giants</h2>
      <p>
        Most MLP screeners stop at energy midstream, but the partnership structure
        applies to a wider set of business types. Worth knowing about:
      </p>
      <ul>
        <li><strong>Energy midstream (the biggest bucket)</strong> — EPD, ET, MPLX, PAA,
          WES, MMLP, DKL, GEL. Pipelines, storage, processing.</li>
        <li><strong>LNG &amp; export terminals</strong> — CQP (Cheniere Energy Partners),
          NMM (Navios Maritime Partners), DLNG (Dynagas LNG Partners).</li>
        <li><strong>Resource &amp; mineral royalties</strong> — BSM (Black Stone Minerals),
          DMLP (Dorchester Minerals), KRP (Kimbell Royalty), NRP (Natural Resource Partners).
          These collect royalties from energy producers; lower capex, more variable cash flow.</li>
        <li><strong>Renewables &amp; infrastructure (Brookfield pass-through vehicles)</strong> —
          BIP (Brookfield Infrastructure), BEP (Brookfield Renewable). Bermuda LPs, K-1
          issuers, but the asset base is utilities/data centers/wind/solar — a very
          different exposure than the energy MLPs.</li>
        <li><strong>Specialised &amp; retail fuel</strong> — SUN (Sunoco), GLP (Global
          Partners), USAC (USA Compression).</li>
        <li><strong>Financial-services MLP</strong> — AB (AllianceBernstein Holding) is
          the one financial partnership most retail investors come across.</li>
      </ul>

      <h2>When MLPs make sense in a portfolio</h2>
      <ul>
        <li>✅ Taxable brokerage account</li>
        <li>✅ Investor comfortable with K-1 tax filing OR using tax software that handles it</li>
        <li>✅ Desire for 7-9% yield with infrastructure-backed cash flow</li>
        <li>✅ Willing to hold for years (avoid frequent buys/sells — every sale triggers a basis recapture)</li>
      </ul>

      <h2>When MLPs don't make sense</h2>
      <ul>
        <li>❌ Inside an IRA (UBTI problem)</li>
        <li>❌ Investor who hates tax complexity (K-1 is enough friction to be a no for many people)</li>
        <li>❌ Need for capital appreciation as much as income (MLPs are slow growers)</li>
        <li>❌ Concerned about energy-sector concentration risk</li>
      </ul>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Find MLPs in DiviDrip</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, search for tickers like{' '}
        <strong>EPD</strong>, <strong>ET</strong>, <strong>MPLX</strong>,{' '}
        <strong>WES</strong>, <strong>BIP</strong>, or <strong>BSM</strong>. Two
        on-page signals confirm you're looking at a partnership rather than a
        regular C-corp:
      </p>
      <ul>
        <li>The <strong>Triangle tab</strong> shows an amber <em>"MLP note"</em> ribbon
          at the top, plus an "Open Cash Lens" link. The narrative panel cites DCF
          instead of FFO/EPS where relevant.</li>
        <li>The <strong>Stock Metrics tab</strong> renders a dedicated{' '}
          <em>MLP Cash Lens</em> panel — Factory icon, amber border, DCF-based payout
          ratio, and a Sweet Spot / Stretched / Risky verdict that mirrors the bands
          MLP management teams target in their own coverage-ratio disclosures.</li>
      </ul>
      <p>
        Use the Triangle Score as a rough quality filter (higher is better, but
        ignore the trap warning suppression — it's intentional for MLPs), then
        anchor the buy/sell decision on the DCF payout in the Cash Lens.
      </p>
      <p className="text-xs text-muted-foreground italic mt-6">
        Not tax advice. K-1 filings and UBTI rules are complex — consult a tax
        professional before committing significant capital to MLPs.
      </p>
    </LearnPage>
  );
}
